import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from './api';

export type ListResponse<T> = {
  data: T[];
  pagination?: { total: number; offset: number; limit: number };
};

/**
 * Shared CRUD helpers for the settings/HRM/users admin pages. Every list
 * endpoint follows the same `{ data: T[] }` shape, and every mutation
 * invalidates the cached list.
 */
export function useCrud<TRow, TCreate, TUpdate>(opts: {
  key: string;
  /** Path under /api (e.g. "/settings/lead-statuses"). */
  path: string;
  /** Query string appended to the list request (e.g. "include_inactive=true"). */
  listQuery?: string;
}) {
  const { key, path, listQuery } = opts;
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: [key] });

  const list = useQuery({
    queryKey: [key, listQuery ?? ''],
    queryFn: () =>
      apiRequest<ListResponse<TRow>>(`${path}${listQuery ? `?${listQuery}` : ''}`),
  });

  const create = useMutation({
    mutationFn: (body: TCreate) => apiRequest<{ data: TRow }>(path, { method: 'POST', body }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: (args: { id: string; body: TUpdate }) =>
      apiRequest<{ data: TRow }>(`${path}/${args.id}`, { method: 'PATCH', body: args.body }),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`${path}/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return { list, create, update, remove, invalidate };
}
